import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@rematter/ui-web';
import { Trash2 } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface VoidSalesOrderDialogProps {
  salesOrderId: string;
  onVoid: (
    salesOrderId: string,
    forceUnlink?: boolean,
  ) => Promise<{
    deleted?: boolean;
    blockingLoads?: Array<{ id: string; documentNumber: string }>;
    unlinkableLoads?: Array<{ id: string; documentNumber: string }>;
  }>;
  onCancel?: () => void;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export const VoidSalesOrderDialog: React.FC<VoidSalesOrderDialogProps> = ({
  salesOrderId,
  onVoid,
  onCancel,
  message,
  confirmLabel,
  cancelLabel,
}) => {
  const t = useTranslations();
  const [blockingLoads, setBlockingLoads] = React.useState<
    Array<{ id: string; documentNumber: string }>
  >([]);
  const [unlinkableLoads, setUnlinkableLoads] = React.useState<
    Array<{ id: string; documentNumber: string }>
  >([]);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  // i18n fallbacks
  const i18nMessage = message ?? t.voidSalesOrderDialog.message(1);
  const i18nConfirm = confirmLabel ?? t.voidSalesOrderDialog.confirm;
  const i18nCancel = cancelLabel ?? t.voidSalesOrderDialog.cancel;
  const i18nTitle = t.voidSalesOrderDialog.title(1);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const result = await onVoid(salesOrderId, false);

      // Check if there are blocking loads
      if (result.blockingLoads && result.blockingLoads.length > 0) {
        setBlockingLoads(result.blockingLoads);
        return;
      }

      // Check if there are unlinkable loads requiring confirmation
      // If deleted is false and there are unlinkable loads, we need confirmation
      if (
        !result.deleted &&
        result.unlinkableLoads &&
        result.unlinkableLoads.length > 0
      ) {
        setUnlinkableLoads(result.unlinkableLoads);
        setShowConfirmation(true);
        return;
      }
    } catch (error) {
      console.error('Error voiding sales order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceUnlink = async () => {
    try {
      setIsLoading(true);
      await onVoid(salesOrderId, true);
    } catch (error) {
      console.error('Error force unlinking sales order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show blocking loads error
  if (blockingLoads.length > 0) {
    return (
      <DialogOverlay isOpen className="@VoidSalesOrderDialog">
        <DialogContent>
          <DialogHeader>
            <div className="text-base font-semibold">
              Cannot Void Sales Order
            </div>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-4">
            <div className="text-base text-neutral/50">
              This sales order cannot be voided because the following loads have
              already been shipped, received, or reconciled:
            </div>
            <ul className="list-disc pl-5">
              {blockingLoads.map((load) => (
                <li key={load.id}>Load #{load.documentNumber}</li>
              ))}
            </ul>
            <div className="flex justify-end gap-2 py-4">
              <Button variant="solid" color="primary" onPress={onCancel}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogOverlay>
    );
  }

  // Show unlinkable loads confirmation
  if (showConfirmation && unlinkableLoads.length > 0) {
    return (
      <DialogOverlay isOpen className="@VoidSalesOrderDialog">
        <DialogContent>
          <DialogHeader>
            <div className="text-base font-semibold">
              Confirm Void with Load Unlinking
            </div>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-4">
            <div className="text-base text-neutral/50">
              Voiding this sales order will unlink the following
              pending/unassigned loads:
            </div>
            <ul className="list-disc pl-5">
              {unlinkableLoads.map((load) => (
                <li key={load.id}>Load #{load.documentNumber}</li>
              ))}
            </ul>
            <div className="mt-2 text-base text-neutral/50">
              Do you want to proceed with voiding and unlinking these loads?
            </div>
            <div className="flex justify-end gap-2 py-4">
              <Button variant="text" color="neutral" onPress={onCancel}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color="failure"
                onPress={handleForceUnlink}
                icon={<Trash2 className="size-4" />}
                isDisabled={isLoading}
              >
                Void and Unlink
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogOverlay>
    );
  }

  // Initial confirmation dialog
  return (
    <DialogOverlay isOpen className="@VoidSalesOrderDialog">
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">{i18nTitle}</div>
        </DialogHeader>
        <div className="flex flex-col gap-2 px-4">
          <div className="text-base text-neutral/50">{i18nMessage}</div>
          <div className="flex justify-end gap-2 py-4">
            <Button variant="text" color="neutral" onPress={onCancel}>
              {i18nCancel}
            </Button>
            <Button
              variant="solid"
              color="failure"
              onPress={handleConfirm}
              icon={<Trash2 className="size-4" />}
              isDisabled={isLoading}
            >
              {i18nConfirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
